import { useEffect, useMemo, useState } from "react";

import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import axiosServices from "../../../../../utils/axios";
import Loader from "../../../../../ui-component/Loader";
import ConfirmationModal from "../../../../../components/modal/ConfirmationModal";
import { useDispatch, useSelector } from "../../../../../store";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  deleteExercise,
  fetchExercises,
  getExercise,
  startLoading,
  stopLoading,
} from "../../../../../store/slices/exercises";
import { SERVER_URL } from "../../../../../config";
import AppPagination from "../../../../../components/common/AppPagination";

export default function DataList({
  search = "",
  searchField = { name: "Title", value: "name" },
}) {
  const [paging, setPaging] = useState({ limit: 10, currentPage: 1 });

  const dispatch = useDispatch();
  const { exercises, loading } = useSelector((state) => state.exercise);

  // Engagement metrics (favorited / saved counts), keyed by exercise id.
  const [metricsMap, setMetricsMap] = useState({});
  const [sortMetric, setSortMetric] = useState("");
  useEffect(() => {
    axiosServices
      .get("/uni/exercise-metrics")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((m) => {
          map[m.id] = { favorited: m.favorited || 0, saved: m.saved || 0 };
        });
        setMetricsMap(map);
      })
      .catch(() => setMetricsMap({}));
  }, []);

  const searchedExercises = useMemo(() => {
    const regex = new RegExp(
      search
        .split(" ")
        .map((word) => `(?=.*${word})`)
        .join(""),
      "i"
    );

    const field = searchField?.value?.length > 0 ? searchField.value : "name";
    const filtered = exercises.filter((x) => regex.test(x[field]));
    if (sortMetric) {
      return [...filtered].sort(
        (a, b) =>
          (metricsMap[b?.id]?.[sortMetric] || 0) -
          (metricsMap[a?.id]?.[sortMetric] || 0)
      );
    }
    return filtered;
  }, [search, searchField, exercises, sortMetric, metricsMap]);

  const currentPageData = useMemo(() => {
    const { limit, currentPage } = paging;
    const min = limit * (currentPage - 1);
    const max = limit * currentPage + 1;
    return searchedExercises.slice(min, max);
  }, [paging, searchedExercises]);

  const col = ["Image", "Title", "Subtitle", "Description", "Favorited", "Saved"];

  useEffect(() => {
    if (exercises.length === 0) {
      dispatch(fetchExercises());
    }
  }, [exercises]);

  return (
    <>
      {loading && <Loader />}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 1 }}>
        <Typography variant="body2">Sort by:</Typography>
        <Select
          size="small"
          value={sortMetric}
          onChange={(e) => setSortMetric(e.target.value)}
          displayEmpty
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Default</MenuItem>
          <MenuItem value="favorited">Most favorited</MenuItem>
          <MenuItem value="saved">Most saved</MenuItem>
        </Select>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {col.map((item, index) => (
                <TableCell key={index}>{item}</TableCell>
              ))}
              <TableCell align="center" sx={{ pr: 3 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchedExercises.length > 0 &&
              currentPageData.map((exercise) => (
                <ExerciseRow
                  key={exercise.id}
                  exercise={exercise}
                  metrics={metricsMap[exercise.id]}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AppPagination
        data={searchedExercises}
        totalPages={Math.ceil(searchedExercises.length / paging.limit)}
        onChangePage={(e, val) => {
          setPaging({ ...paging, currentPage: val });
        }}
      />
    </>
  );
}

export const ExerciseRow = ({ exercise, metrics = {} }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const onClickEdit = () => {
    navigate(`/edit-exercise/${exercise.id}`);
  };

  const onClickDelete = () => {
    setOpen(true);
  };

  const onPressYes = () => {
    dispatch(startLoading());
    dispatch(
      deleteExercise(
        exercise.id,
        () => {
          stopLoading();
        },
        () => {
          stopLoading();
        }
      )
    );
    setOpen(false);
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <img
            onError={(e) => {
              e.target.src =
                "https://i.postimg.cc/W32qxZ91/placeholder-image.png";
            }}
            style={{
              width: 100,
              height: 100,
              objectFit: "contain",
              borderRadius: 10,
              border: "1px solid #eee",
            }}
            src={SERVER_URL + exercise.image_meta?.url}
            alt={exercise.description}
          />
        </TableCell>
        <TableCell>
          <Typography align="left" variant="subtitle1" component="div">
            {exercise?.name}
            <div>Exercise Type: {exercise.exerciseType}</div>
          </Typography>
        </TableCell>

        <TableCell>
          <Typography align="left" variant="subtitle1" component="div">
            {exercise?.subName}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography align="left" variant="subtitle1" component="div">
            {exercise.description}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography align="center" variant="subtitle1" component="div">
            {metrics?.favorited ?? 0}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography align="center" variant="subtitle1" component="div">
            {metrics?.saved ?? 0}
          </Typography>
        </TableCell>

        <TableCell align="center" sx={{ pr: 3 }}>
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Tooltip placement="top" title="Edit">
              <IconButton
                style={{ color: "green" }}
                aria-label="delete"
                size="large"
                onClick={onClickEdit}
              >
                <EditIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip placement="top" title="Delete">
              <IconButton
                style={{ color: "red" }}
                aria-label="delete"
                size="large"
                onClick={onClickDelete}
              >
                <DeleteIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      <ConfirmationModal
        open={open}
        setOpen={setOpen}
        onPress={onPressYes}
        text="Are you sure, you want to delete exercise?"
      />
    </>
  );
};
